/**
 * AI Service
 * Project Risk & Complexity Assessment
 *
 * Typed wrapper around the Cloudflare Function at /api/ai.
 * Manages per-question conversation history and provides a
 * local keyword-matching fallback when AI is unavailable.
 *
 * @version 2.0.0
 */

import type {
  AIMode,
  AIMessage,
  AIMapping,
  AIRecommendation,
  DocumentAnalysisResult,
  Question,
  AssessmentResult,
  RCAProject,
  AnswerOption,
} from '../types/rca';

// ============================================================
// Constants
// ============================================================

const AI_ENDPOINT = '/api/ai';
const MAX_HISTORY_MESSAGES = 20;

// ============================================================
// Per-question conversation history (module-level state)
// ============================================================

const _histories = new Map<string, AIMessage[]>();

function historyKey(questionId: string): string {
  return questionId;
}

function getHistory(questionId: string): AIMessage[] {
  const key = historyKey(questionId);
  if (!_histories.has(key)) {
    _histories.set(key, []);
  }
  return _histories.get(key)!;
}

function pushHistory(questionId: string, role: 'user' | 'assistant', content: string): void {
  const h = getHistory(questionId);
  h.push({ role, content });
  // Cap at MAX_HISTORY_MESSAGES
  if (h.length > MAX_HISTORY_MESSAGES) {
    h.splice(0, h.length - MAX_HISTORY_MESSAGES);
  }
}

// ============================================================
// API call helper
// ============================================================

interface AIProxyRequest {
  mode: AIMode;
  conversation: AIMessage[];
  question?: QuestionPayload;
  project?: Partial<{ name: string; department: string }>;
  [key: string]: unknown;
}

interface AIProxyResponse {
  message?: string;
  mapping?: AIMapping;
  recommendations?: AIRecommendation[];
  answers?: DocumentAnalysisResult['answers'];
  [key: string]: unknown;
}

interface QuestionPayload {
  text: string;
  answers: Array<{ label: string; text: string; multiplier: number }>;
  weight: number;
  phase: string;
  section: string;
}

function buildQuestionPayload(question: Question, phaseId?: string, section?: string): QuestionPayload {
  return {
    text: question.text,
    answers: question.answers.map((a: AnswerOption) => ({
      label: a.l,
      text: a.t,
      multiplier: a.m,
    })),
    weight: question.weight,
    phase: phaseId ?? '',
    section: section ?? '',
  };
}

async function callProxy(request: AIProxyRequest): Promise<AIProxyResponse | null> {
  try {
    const res = await fetch(AI_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!res.ok) return null;
    return res.json() as Promise<AIProxyResponse>;
  } catch {
    return null;
  }
}

// ============================================================
// PUBLIC: assess
// ============================================================

/**
 * Send a user message for assessment of a specific question.
 * Falls back to local keyword matching if AI is unavailable.
 *
 * @param userMessage   - Free-text user input
 * @param questionId    - Unique question identifier (used as history key)
 * @param questionContext - The question being answered
 * @returns Message text and optional answer mapping
 */
export async function assess(
  userMessage: string,
  questionId: string,
  questionContext: Question
): Promise<{ message: string; mapping: AIMapping | null }> {
  pushHistory(questionId, 'user', userMessage);
  const history = getHistory(questionId);

  try {
    const result = await callProxy({
      mode: 'assess',
      conversation: [...history],
      question: buildQuestionPayload(questionContext),
    });

    if (result?.message) {
      pushHistory(questionId, 'assistant', result.message);
      return { message: result.message, mapping: result.mapping ?? null };
    }
  } catch {
    // fall through to local fallback
  }

  // Local keyword fallback
  const localResult = localMap(userMessage, questionContext.answers);
  const msg = localResult
    ? 'I mapped your response locally (AI unavailable). Please confirm the selection.'
    : 'AI is currently unavailable. Please select an answer manually.';
  pushHistory(questionId, 'assistant', msg);
  return { message: msg, mapping: localResult };
}

// ============================================================
// PUBLIC: clarify
// ============================================================

/**
 * Request clarification on a user's previous response.
 */
export async function clarify(
  userMessage: string,
  questionId: string,
  questionContext: Question
): Promise<{ message: string; mapping: AIMapping | null }> {
  pushHistory(questionId, 'user', userMessage);
  const history = getHistory(questionId);

  try {
    const result = await callProxy({
      mode: 'clarify',
      conversation: [...history],
      question: buildQuestionPayload(questionContext),
    });

    if (result?.message) {
      pushHistory(questionId, 'assistant', result.message);
      return { message: result.message, mapping: result.mapping ?? null };
    }
  } catch {
    // fall through
  }

  const msg = 'AI is currently unavailable. Please select an answer manually.';
  pushHistory(questionId, 'assistant', msg);
  return { message: msg, mapping: null };
}

// ============================================================
// PUBLIC: explain
// ============================================================

/**
 * Ask AI to explain a question in plain language.
 */
export async function explain(questionContext: Question): Promise<string> {
  const conversation: AIMessage[] = [
    { role: 'user', content: 'Please explain: ' + questionContext.text },
  ];

  try {
    const result = await callProxy({
      mode: 'explain',
      conversation,
      question: buildQuestionPayload(questionContext),
    });
    if (result?.message) return result.message;
  } catch {
    // fall through
  }

  return questionContext.help
    ? questionContext.help
    : 'AI is currently unavailable. Check the help text next to the question for guidance.';
}

// ============================================================
// PUBLIC: recommend
// ============================================================

/**
 * Request AI-generated risk mitigation recommendations.
 */
export async function recommend(
  assessment: AssessmentResult,
  projectContext: RCAProject
): Promise<AIRecommendation[]> {
  const summary = buildAssessmentSummary(assessment, projectContext);

  const result = await callProxy({
    mode: 'recommend',
    conversation: [
      { role: 'user', content: 'Generate risk recommendations for this project assessment.' },
    ],
    project: {
      name: projectContext.name,
      department: projectContext.department,
    },
    assessmentContext: summary,
    assessment: {
      category: assessment.overall.category,
      avgRisk: assessment.overall.avgRisk,
      avgComplexity: assessment.overall.avgComplexity,
      riskLevel: assessment.overall.riskLevel,
      complexityLevel: assessment.overall.complexityLevel,
      activePhases: assessment.activePhases,
    },
  });

  if (!result) {
    throw new Error('AI service unreachable');
  }

  // Parse recommendations from response
  return parseRecommendations(result);
}

// ============================================================
// PUBLIC: analyzeDocument
// ============================================================

/**
 * Submit document text for AI to extract assessment answers.
 */
export async function analyzeDocument(
  content: string,
  questions: Question[]
): Promise<DocumentAnalysisResult> {
  const result = await callProxy({
    mode: 'analyze-document',
    conversation: [
      { role: 'user', content: 'Extract answers from this project document.' },
    ],
    document: content,
    questions: questions.map((q) => ({
      id: q.num,
      text: q.text,
      answers: q.answers.map((a: AnswerOption) => ({ label: a.l, text: a.t })),
    })),
  });

  if (!result) {
    throw new Error('AI service unreachable');
  }

  return {
    answers: Array.isArray(result.answers) ? result.answers : [],
  };
}

// ============================================================
// PUBLIC: conversation history management
// ============================================================

export function getConversationHistory(questionId: string): AIMessage[] {
  return [...getHistory(questionId)];
}

export function clearConversation(questionId: string): void {
  _histories.delete(historyKey(questionId));
}

// ============================================================
// LOCAL MAP — keyword/pattern fallback (ported from chat-ai.js)
// ============================================================

/**
 * Attempt to map free-text input to an answer index using local
 * keyword and pattern matching. Returns null when ambiguous.
 */
export function localMap(
  userInput: string,
  answers: AnswerOption[]
): AIMapping | null {
  if (!userInput || answers.length === 0) return null;
  const input = userInput.trim().toLowerCase();

  // 1. Dollar amount parsing for cost questions
  const dollarAmount = parseDollarAmount(input);
  if (dollarAmount !== null) {
    const costIdx = mapCostToAnswer(dollarAmount, answers);
    if (costIdx !== null) {
      return { answerIndex: costIdx, confidence: 'medium', label: answers[costIdx].l };
    }
  }

  // 2. Yes / No patterns
  if (/^(yes|yeah|yep|yup|correct|affirmative|absolutely|definitely)\b/.test(input)) {
    const yesIdx = findAnswerByLabel(answers, 'yes');
    if (yesIdx !== null) {
      return { answerIndex: yesIdx, confidence: 'high', label: answers[yesIdx].l };
    }
  }
  if (/^(no|nope|nah|negative|not really)\b/.test(input)) {
    const noIdx = findAnswerByLabel(answers, 'no');
    if (noIdx !== null) {
      return { answerIndex: noIdx, confidence: 'high', label: answers[noIdx].l };
    }
  }
  if (/not sure|don't know|unsure|maybe|i guess|uncertain/.test(input)) {
    return null; // needs clarification
  }

  // 3. Keyword matching against answer text
  let bestIdx: number | null = null;
  let bestScore = 0;

  for (let i = 0; i < answers.length; i++) {
    const ansText = (answers[i].t ?? '').toLowerCase();
    const words = ansText.split(/\W+/).filter((w) => w.length > 3);
    let score = 0;
    for (const word of words) {
      if (input.includes(word)) score++;
    }
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }

  if (bestIdx !== null && bestScore >= 2) {
    return { answerIndex: bestIdx, confidence: 'medium', label: answers[bestIdx].l };
  }
  if (bestIdx !== null && bestScore === 1) {
    return { answerIndex: bestIdx, confidence: 'low', label: answers[bestIdx].l };
  }

  return null;
}

// ============================================================
// Internal: dollar amount parsing
// ============================================================

const WORD_MULTIPLIERS: Record<string, number> = {
  thousand: 1000, k: 1000,
  million: 1000000, m: 1000000, mil: 1000000,
  billion: 1000000000, b: 1000000000,
};

const NUMBER_WORDS: Record<string, number> = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
  eight: 8, nine: 9, ten: 10, twenty: 20, thirty: 30, forty: 40,
  fifty: 50, hundred: 100, half: 0.5, quarter: 0.25,
};

function parseDollarAmount(input: string): number | null {
  // Pattern: "$2,500,000" or "2500000" or "$2M"
  const rawNum = input.replace(/[$,]/g, '').match(/(\d+(?:\.\d+)?)\s*(k|m|mil|million|thousand|billion|b)?/i);
  if (rawNum) {
    const val = parseFloat(rawNum[1]);
    const suffix = (rawNum[2] ?? '').toLowerCase();
    const multiplier = WORD_MULTIPLIERS[suffix];
    return multiplier ? val * multiplier : val;
  }

  // Pattern: "two million", "half a million"
  for (const [word, numVal] of Object.entries(NUMBER_WORDS)) {
    if (input.includes(word)) {
      for (const [mult, multVal] of Object.entries(WORD_MULTIPLIERS)) {
        if (input.includes(mult)) {
          return numVal * multVal;
        }
      }
    }
  }

  return null;
}

function parseBracket(text: string): { min: number; max: number } | null {
  const nums: number[] = [];
  const re = /\$?([\d,]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    nums.push(parseInt(m[1].replace(/,/g, ''), 10));
  }
  if (text.includes('>') && nums.length >= 1) {
    return { min: nums[0] + 1, max: Infinity };
  }
  if (text.includes('<') && nums.length >= 1) {
    return { min: 0, max: nums[0] - 1 };
  }
  if (nums.length >= 2) {
    return { min: Math.min(nums[0], nums[1]), max: Math.max(nums[0], nums[1]) };
  }
  return null;
}

function mapCostToAnswer(amount: number, answers: AnswerOption[]): number | null {
  for (let i = 0; i < answers.length; i++) {
    const range = parseBracket(answers[i].t);
    if (range && amount >= range.min && amount <= range.max) {
      return i;
    }
  }
  return null;
}

function findAnswerByLabel(answers: AnswerOption[], keyword: string): number | null {
  for (let i = 0; i < answers.length; i++) {
    if ((answers[i].t ?? '').toLowerCase().startsWith(keyword)) {
      return i;
    }
  }
  return null;
}

// ============================================================
// Internal: parse AI recommendation response
// ============================================================

function parseRecommendations(json: AIProxyResponse): AIRecommendation[] {
  let data: unknown[] | null = null;

  if (Array.isArray(json)) {
    data = json as unknown[];
  } else if (json.message) {
    try {
      const cleaned = json.message
        .replace(/```json\s*/g, '')
        .replace(/```\s*/g, '')
        .trim();
      const parsed = JSON.parse(cleaned) as unknown;
      if (Array.isArray(parsed)) data = parsed;
    } catch {
      data = null;
    }
  }

  if (!data || data.length === 0) {
    throw new Error('AI returned an unexpected response format. Try again.');
  }

  const validated: AIRecommendation[] = [];
  for (const item of data.slice(0, 5)) {
    const rec = item as Record<string, unknown>;
    if (!rec || typeof rec !== 'object') continue;
    validated.push({
      area: String(rec.area ?? 'General Risk'),
      action: String(rec.action ?? ''),
      impact: String(rec.impact ?? ''),
      priority: normalizePriority(rec.priority),
      governance_reference: String(rec.governance_reference ?? ''),
    });
  }

  if (validated.length === 0) {
    throw new Error('No valid recommendations in AI response.');
  }

  return validated;
}

function normalizePriority(val: unknown): 'High' | 'Medium' | 'Low' {
  if (!val) return 'Medium';
  const lower = String(val).toLowerCase().trim();
  if (lower === 'high' || lower === 'critical') return 'High';
  if (lower === 'low' || lower === 'minor') return 'Low';
  return 'Medium';
}

// ============================================================
// Internal: build assessment summary text for AI context
// ============================================================

function buildAssessmentSummary(
  assessment: AssessmentResult,
  project: RCAProject
): string {
  const lines: string[] = [];
  lines.push(`Project: ${project.name}`);
  lines.push(`Department: ${project.department}`);
  lines.push(`Category: ${assessment.overall.category ?? 'Not scored'}`);
  lines.push(
    `Risk Level: ${assessment.overall.riskLevel} (Score: ${
      assessment.overall.avgRisk != null ? Math.round(assessment.overall.avgRisk) : 'N/A'
    }/500)`
  );
  lines.push(
    `Complexity Level: ${assessment.overall.complexityLevel} (Score: ${
      assessment.overall.avgComplexity != null ? Math.round(assessment.overall.avgComplexity) : 'N/A'
    }/500)`
  );
  lines.push(`Active Phases: ${assessment.activePhases.join(', ')}`);

  const answeredCount = Object.keys(assessment.answers).filter(
    (k) => assessment.answers[k] !== undefined && assessment.answers[k] !== null
  ).length;
  lines.push(`Questions Answered: ${answeredCount}`);

  return lines.join('\n');
}
